import { useCallback, useMemo } from "react";
import { trpc } from "../trpc";

const EMPTY_GUARDS: never[] = [];

// Compliance detection (used for status labels in ConnectionRow and the
// "disable managed apps" guard in AddConnectionModal) must always be
// accurate — it is not gated behind assetmanagement_managed_connections_filter.
// That split only controls the "Managed" *filter UI* (see
// ConnectionSharingFilterController / SharingFilter / useConnectionsFilterConfig),
// which check the flag directly and don't go through this hook.
export function useQueryManagedGuards() {
  const { data, isLoading, isError } = trpc.guard.getGuards.useQuery(
    {
      guardType: "managed_auth_only",
      isActive: true,
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );

  const guards = data ?? EMPTY_GUARDS;

  const { managedSelectedApis, isAllAppsManaged } = useMemo(() => {
    const managedSelectedApis = new Set<string>();
    let isAllAppsManaged = false;

    for (const guard of guards) {
      if (guard.selectedApis === "all") {
        isAllAppsManaged = true;
      } else {
        for (const api of guard.selectedApis) {
          managedSelectedApis.add(api);
        }
      }
    }

    return { managedSelectedApis, isAllAppsManaged };
  }, [guards]);

  // Strip version suffix (e.g. "GoogleMailV2CLIAPI@1.0.0" -> "GoogleMailV2CLIAPI")
  // because guard selectedApis are versionless
  const isAppManaged = useCallback(
    (appId: string) => {
      const strippedId = appId?.split("@")[0] ?? "";
      return strippedId
        ? isAllAppsManaged || managedSelectedApis.has(strippedId)
        : false;
    },
    [isAllAppsManaged, managedSelectedApis],
  );

  return {
    isAppManaged,
    isLoading,
    isError,
  };
}
